import React from 'react';
import {
  Box,
  Typography,
  Card,
  LinearProgress,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ChartBarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

export interface FeedbackData {
  overallScore: number;
  correctAnswers: number;
  totalQuestions: number;
  categories: {
    name: string;
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    questions: {
      question: string;
      userAnswer: string;
      isCorrect: boolean;
      feedback?: string;
    }[];
  }[];
  trainingDuration: number;
  recommendations?: string[];
}

interface TrainingFeedbackProps {
  feedbackData?: FeedbackData;
  isLoading: boolean;
  onNewTraining: () => void;
}

export const TrainingFeedback: React.FC<TrainingFeedbackProps> = ({
  feedbackData,
  isLoading,
  onNewTraining,
}) => {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return '#4CAF50'; // Green
    if (percentage >= 60) return '#FF9800'; // Orange
    return '#F44336'; // Red
  };

  const getScoreMessage = (percentage: number) => {
    if (percentage >= 80)
      return 'Excellent! Anda menguasai materi dengan baik.';
    if (percentage >= 60)
      return 'Good! Masih ada beberapa area yang perlu diperbaiki.';
    return 'Perlu belajar lebih lanjut. Coba ulangi training.';
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <img
          src="https://dopmo1eihgbgm.cloudfront.net/68b81d7b27352545f9c422d2/jago.png"
          alt="Jago Coffee"
          style={{ height: '60px', marginBottom: '30px' }}
        />
        <CircularProgress size={60} sx={{ color: '#D2691E', mb: 3 }} />
        <Typography variant="h6" sx={{ color: '#8B4513', mb: 1 }}>
          Menganalisis Hasil Training...
        </Typography>
        <Typography variant="body2" sx={{ color: '#A0522D' }}>
          Mohon tunggu sebentar
        </Typography>
      </Box>
    );
  }

  // No data state
  if (!feedbackData) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <Typography variant="h6" sx={{ color: '#8B4513', mb: 2 }}>
          Tidak dapat memuat hasil training
        </Typography>
        <Box
          onClick={onNewTraining}
          sx={{
            px: 4,
            py: 2,
            bgcolor: '#D2691E',
            color: 'white',
            borderRadius: 2,
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
            '&:hover': {
              bgcolor: '#CD853F',
            },
          }}
        >
          Mulai Training Baru
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        py: 4,
      }}
    >
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: 2 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: '#8B4513', mb: 2 }}
          >
            Hasil Training Jago Coffee
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <ClockIcon className="w-5 h-5 text-[#A0522D]" />
            <Typography variant="body1" sx={{ color: '#A0522D' }}>
              Waktu training: {formatTime(feedbackData.trainingDuration)}
            </Typography>
          </Box>
        </Box>

        {/* Overall Score */}
        <Card
          sx={{ p: 4, mb: 4, bgcolor: '#FFF8DC', border: '1px solid #DEB887' }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <ChartBarIcon
                className="w-10 h-10"
                style={{ color: getScoreColor(feedbackData.overallScore) }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: getScoreColor(feedbackData.overallScore),
                }}
              >
                {Math.round(feedbackData.overallScore)}%
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ color: '#8B4513', mb: 3 }}>
              {feedbackData.correctAnswers} dari {feedbackData.totalQuestions}{' '}
              pertanyaan dijawab dengan benar
            </Typography>
            <LinearProgress
              variant="determinate"
              value={feedbackData.overallScore}
              sx={{
                height: 12,
                borderRadius: 6,
                bgcolor: '#F5DEB3',
                mb: 3,
                '& .MuiLinearProgress-bar': {
                  bgcolor: getScoreColor(feedbackData.overallScore),
                  borderRadius: 6,
                },
              }}
            />
            <Typography
              variant="body1"
              sx={{ color: '#A0522D', fontWeight: 500 }}
            >
              {getScoreMessage(feedbackData.overallScore)}
            </Typography>
          </Box>
        </Card>

        {/* Category Results */}
        {feedbackData.categories.map((category, categoryIndex) => (
          <Card
            key={categoryIndex}
            sx={{ mb: 3, border: '1px solid #DEB887', overflow: 'hidden' }}
          >
            <Box
              sx={{
                p: 3,
                bgcolor: '#FFF8DC',
                borderBottom: '1px solid #DEB887',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: '#8B4513' }}
              >
                {category.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: '#A0522D' }}>
                  {Math.round(category.score)}%
                </Typography>
                <Chip
                  label={`${category.correctAnswers}/${category.totalQuestions}`}
                  color={
                    category.correctAnswers === category.totalQuestions
                      ? 'success'
                      : category.correctAnswers > category.totalQuestions / 2
                      ? 'warning'
                      : 'error'
                  }
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            <Box sx={{ p: 3 }}>
              {category.questions.map((item, questionIndex) => (
                <Box
                  key={questionIndex}
                  sx={{
                    mb: 3,
                    pb: 3,
                    borderBottom:
                      questionIndex < category.questions.length - 1
                        ? '1px solid #F5DEB3'
                        : 'none',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                  >
                    <Box sx={{ mt: 0.5 }}>
                      {item.isCorrect ? (
                        <CheckCircleIcon className="w-6 h-6 text-green-500" />
                      ) : (
                        <XCircleIcon className="w-6 h-6 text-red-500" />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#5D4037',
                          mb: 1,
                          lineHeight: 1.4,
                        }}
                      >
                        {item.question}
                      </Typography>

                      <Card
                        sx={{
                          p: 2,
                          mb: 2,
                          bgcolor: item.isCorrect ? '#E8F5E8' : '#FFF3E0',
                          border: `1px solid ${
                            item.isCorrect ? '#C8E6C9' : '#FFCC02'
                          }`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#5D4037',
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          <strong>Jawaban Anda:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#8B4513' }}>
                          {item.userAnswer}
                        </Typography>
                      </Card>

                      {item.feedback && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            p: 2,
                            bgcolor: '#F5F5F5',
                            borderRadius: 2,
                            border: '1px solid #E0E0E0',
                          }}
                        >
                          <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#666',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              Feedback
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: '#333', mt: 0.5 }}
                            >
                              {item.feedback}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        ))}

        {/* Recommendations */}
        {feedbackData.recommendations &&
          feedbackData.recommendations.length > 0 && (
            <Card sx={{ mb: 4, border: '1px solid #DEB887' }}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: '#FFF8DC',
                  borderBottom: '1px solid #DEB887',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: '#8B4513' }}
                >
                  Rekomendasi Pembelajaran
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                {feedbackData.recommendations.map((recommendation, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: '#D2691E',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#5D4037', flex: 1 }}
                    >
                      {recommendation}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          )}

        {/* Actions */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Box
            onClick={onNewTraining}
            sx={{
              display: 'inline-block',
              px: 6,
              py: 3,
              bgcolor: '#D2691E',
              color: 'white',
              borderRadius: 3,
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(210, 105, 30, 0.3)',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: '#CD853F',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(210, 105, 30, 0.4)',
              },
            }}
          >
            {feedbackData.overallScore >= 80 ? 'Training Ulang' : 'Coba Lagi'}
          </Box>

          <Typography variant="body2" sx={{ color: '#A0522D', mt: 3 }}>
            Terima kasih telah mengikuti training Jago Coffee!
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
